import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SelectedClient {
  id: string;
  name: string;
  client_ref: string;
  is_service_provider: boolean;
  framework_scope: string[];
}

interface ClientContextType {
  selectedClient: SelectedClient | null;
  setSelectedClient: (client: SelectedClient | null) => void;
  isLoading: boolean;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const [selectedClient, setSelectedClientState] = useState<SelectedClient | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        // Avoid querying RLS-protected tables before we know we have a session.
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (!session?.user) {
          setIsLoading(false);
          return;
        }

        const stored = localStorage.getItem("selectedClient");
        if (stored) {
          try {
            const parsed: SelectedClient = JSON.parse(stored);

            const { data, error } = await supabase
              .from("client_organisations")
              .select("id, name, client_ref, is_service_provider, framework_scope")
              .eq("id", parsed.id)
              .maybeSingle();

            if (!isMounted) return;

            if (error || !data) {
              localStorage.removeItem("selectedClient");
              setSelectedClientState(null);
            } else {
              setSelectedClientState({
                id: data.id,
                name: data.name,
                client_ref: data.client_ref,
                is_service_provider: data.is_service_provider ?? false,
                framework_scope: data.framework_scope ?? [],
              });
            }
          } catch {
            localStorage.removeItem("selectedClient");
            setSelectedClientState(null);
          }
        }

        setIsLoading(false);
      } catch {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setSelectedClientState(null);
        localStorage.removeItem("selectedClient");
      }

      if (event === "SIGNED_IN") {
        setIsLoading(true);
        // Defer to avoid calling Supabase APIs inside the auth callback.
        setTimeout(() => {
          init();
        }, 0);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const setSelectedClient = useCallback((client: SelectedClient | null) => {
    setSelectedClientState(client);
    if (client) {
      localStorage.setItem("selectedClient", JSON.stringify(client));
    } else {
      localStorage.removeItem("selectedClient");
    }
  }, []);

  const contextValue = useMemo(
    () => ({ selectedClient, setSelectedClient, isLoading }),
    [selectedClient, setSelectedClient, isLoading]
  );

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
    </ClientContext.Provider>
  );
};
